type StructuredCommentTextToken = {
  kind: 'text';
  text: string;
  format?: number;
  style?: string;
};

type StructuredCommentSegmentToken = {
  kind: 'segment';
  segmentId: string;
  text: string;
  format?: number;
  style?: string;
};

export type StructuredCommentToken = StructuredCommentTextToken | StructuredCommentSegmentToken;

export type StructuredCommentLine = StructuredCommentToken[];

export type StructuredCommentAnchorPoint = {
  segmentId: string;
  offset: number;
};

export type StructuredCommentThreadAnchor = {
  threadId: string;
  start: StructuredCommentAnchorPoint;
  end: StructuredCommentAnchorPoint;
};

type PersistedStructuredCommentState = {
  type: 'structured-comment-state-v1';
  anchors: StructuredCommentThreadAnchor[];
};

type LayoutToken = StructuredCommentToken & {
  globalStart: number;
  globalEnd: number;
};

type LayoutSegment = {
  segmentId: string;
  text: string;
  globalStart: number;
  globalEnd: number;
};

type LayoutLine = {
  tokens: LayoutToken[];
};

type StructuredCommentLayout = {
  lines: LayoutLine[];
  segments: LayoutSegment[];
  plainText: string;
};

type GlobalRange = {
  threadId: string;
  start: number;
  end: number;
};

type MutableLexicalNode = {
  type?: string;
  text?: string;
  ids?: string[];
  children?: MutableLexicalNode[];
};

const STRUCTURED_COMMENT_STATE_TYPE = 'structured-comment-state-v1' as const;

const paragraphNode = (children: any[]) => ({
  children,
  direction: 'ltr',
  format: '',
  indent: 0,
  type: 'paragraph',
  version: 1,
  textFormat: 0,
  textStyle: '',
});

const textNode = (text: string, format: number = 0, style: string = '') => ({
  detail: 0,
  format,
  mode: 'normal',
  style,
  text,
  type: 'text',
  version: 1,
});

const highlightNode = (ids: string[], children: any[]) => ({
  type: 'highlight-mark',
  version: 1,
  ids,
  children,
});

const isParagraphLikeNode = (node: MutableLexicalNode) =>
  node.type === 'paragraph' || node.type === 'listitem';

const dedupeIds = (ids: string[]) => Array.from(new Set(ids.filter(Boolean)));

const areIdArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

const buildStructuredCommentLayout = (lines: StructuredCommentLine[]): StructuredCommentLayout => {
  const visibleLineTokens = lines
    .map((line) => line.filter((token) => token.text.length > 0))
    .filter((line) => line.length > 0);

  const layoutLines: LayoutLine[] = [];
  const segments: LayoutSegment[] = [];
  const plainTextParts: string[] = [];
  let cursor = 0;

  visibleLineTokens.forEach((line, lineIndex) => {
    const tokens: LayoutToken[] = [];
    const lineText = line.map((token) => token.text).join('');
    plainTextParts.push(lineText);

    line.forEach((token) => {
      const globalStart = cursor;
      cursor += token.text.length;
      const layoutToken: LayoutToken = {
        ...token,
        globalStart,
        globalEnd: cursor,
      };
      tokens.push(layoutToken);

      if (token.kind === 'segment') {
        segments.push({
          segmentId: token.segmentId,
          text: token.text,
          globalStart,
          globalEnd: cursor,
        });
      }
    });

    layoutLines.push({tokens});

    if (lineIndex < visibleLineTokens.length - 1) {
      cursor += 1;
    }
  });

  return {
    lines: layoutLines,
    segments,
    plainText: plainTextParts.join('\n'),
  };
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getGlobalOffsetForAnchorPoint = (
  point: StructuredCommentAnchorPoint,
  layout: StructuredCommentLayout
) => {
  const segment = layout.segments.find(({segmentId}) => segmentId === point.segmentId);

  if (!segment) {
    return null;
  }

  return segment.globalStart + clamp(point.offset, 0, segment.text.length);
};

const getAnchorPointForOffset = (
  offset: number,
  layout: StructuredCommentLayout,
  affinity: 'start' | 'end'
): StructuredCommentAnchorPoint | null => {
  if (layout.segments.length === 0) {
    return null;
  }

  const clampedOffset = clamp(offset, 0, layout.plainText.length);
  const exactSegment = layout.segments.find(
    (segment) => clampedOffset >= segment.globalStart && clampedOffset <= segment.globalEnd
  );

  if (exactSegment) {
    return {
      segmentId: exactSegment.segmentId,
      offset: clampedOffset - exactSegment.globalStart,
    };
  }

  const previousSegment = [...layout.segments]
    .reverse()
    .find((segment) => segment.globalEnd <= clampedOffset);
  const nextSegment = layout.segments.find((segment) => segment.globalStart >= clampedOffset);

  if (affinity === 'start') {
    if (previousSegment) {
      return {
        segmentId: previousSegment.segmentId,
        offset: previousSegment.text.length,
      };
    }
    if (nextSegment) {
      return {
        segmentId: nextSegment.segmentId,
        offset: 0,
      };
    }
    return null;
  }

  if (nextSegment) {
    return {
      segmentId: nextSegment.segmentId,
      offset: 0,
    };
  }
  if (previousSegment) {
    return {
      segmentId: previousSegment.segmentId,
      offset: previousSegment.text.length,
    };
  }
  return null;
};

const getHighlightRangesFromLexicalJson = (serializedState: string): GlobalRange[] => {
  try {
    const parsed = JSON.parse(serializedState) as {root?: MutableLexicalNode};
    const ranges = new Map<string, GlobalRange>();
    let cursor = 0;

    const addRange = (threadId: string, start: number, end: number) => {
      if (!threadId || start >= end) {
        return;
      }

      const existing = ranges.get(threadId);
      if (!existing) {
        ranges.set(threadId, {threadId, start, end});
        return;
      }

      existing.start = Math.min(existing.start, start);
      existing.end = Math.max(existing.end, end);
    };

    const visit = (node: MutableLexicalNode | undefined, activeIds: string[]) => {
      if (!node) {
        return;
      }

      const nextActiveIds =
        node.type === 'highlight-mark' ? dedupeIds([...activeIds, ...(node.ids ?? [])]) : activeIds;

      if (node.type === 'text') {
        const text = node.text ?? '';
        const start = cursor;
        cursor += text.length;
        nextActiveIds.forEach((threadId) => {
          addRange(threadId, start, cursor);
        });
        return;
      }

      node.children?.forEach((child) => {
        visit(child, nextActiveIds);
      });

      if (isParagraphLikeNode(node)) {
        cursor += 1;
      }
    };

    visit(parsed.root, []);

    return Array.from(ranges.values()).sort((left, right) => left.start - right.start);
  } catch {
    return [];
  }
};

export const buildStructuredCommentPlainText = (lines: StructuredCommentLine[]) =>
  buildStructuredCommentLayout(lines).plainText;

export const buildStructuredCommentLexicalValue = (
  lines: StructuredCommentLine[],
  anchors: StructuredCommentThreadAnchor[]
) => {
  const layout = buildStructuredCommentLayout(lines);
  const ranges = anchors
    .map((anchor) => {
      const start = getGlobalOffsetForAnchorPoint(anchor.start, layout);
      const end = getGlobalOffsetForAnchorPoint(anchor.end, layout);

      if (start === null || end === null || start === end) {
        return null;
      }

      return {
        threadId: anchor.threadId,
        start: Math.min(start, end),
        end: Math.max(start, end),
      };
    })
    .filter((range): range is GlobalRange => range !== null);

  const children = layout.lines.map((line) => {
    const pieces: Array<{text: string; format: number; style: string; activeIds: string[]}> = [];

    line.tokens.forEach((token) => {
      const tokenLength = token.text.length;
      if (tokenLength === 0) {
        return;
      }

      const boundaries = new Set([0, tokenLength]);
      ranges.forEach((range) => {
        const intersectionStart = Math.max(range.start, token.globalStart);
        const intersectionEnd = Math.min(range.end, token.globalEnd);
        if (intersectionStart >= intersectionEnd) {
          return;
        }
        boundaries.add(intersectionStart - token.globalStart);
        boundaries.add(intersectionEnd - token.globalStart);
      });

      const sortedBoundaries = Array.from(boundaries).sort((left, right) => left - right);

      for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
        const pieceStart = sortedBoundaries[index];
        const pieceEnd = sortedBoundaries[index + 1];
        if (pieceStart === pieceEnd) {
          continue;
        }

        const globalPieceStart = token.globalStart + pieceStart;
        const globalPieceEnd = token.globalStart + pieceEnd;
        const activeIds = dedupeIds(
          ranges
            .filter((range) => range.start < globalPieceEnd && range.end > globalPieceStart)
            .map((range) => range.threadId)
        );

        const nextPiece = {
          text: token.text.slice(pieceStart, pieceEnd),
          format: token.format ?? 0,
          style: token.style ?? '',
          activeIds,
        };
        const previousPiece = pieces[pieces.length - 1];

        if (
          previousPiece &&
          previousPiece.format === nextPiece.format &&
          previousPiece.style === nextPiece.style &&
          areIdArraysEqual(previousPiece.activeIds, nextPiece.activeIds)
        ) {
          previousPiece.text += nextPiece.text;
        } else {
          pieces.push(nextPiece);
        }
      }
    });

    return paragraphNode(
      pieces.map((piece) => {
        const node = textNode(piece.text, piece.format, piece.style);
        return piece.activeIds.length > 0 ? highlightNode(piece.activeIds, [node]) : node;
      })
    );
  });

  return JSON.stringify({
    root: {
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
      children,
    },
  });
};

export const extractStructuredCommentAnchorsFromLexicalJson = (
  serializedState: string,
  lines: StructuredCommentLine[]
) => {
  const layout = buildStructuredCommentLayout(lines);

  return getHighlightRangesFromLexicalJson(serializedState)
    .map((range) => {
      const start = getAnchorPointForOffset(range.start, layout, 'start');
      const end = getAnchorPointForOffset(range.end, layout, 'end');

      if (!start || !end) {
        return null;
      }

      return {
        threadId: range.threadId,
        start,
        end,
      };
    })
    .filter((anchor): anchor is StructuredCommentThreadAnchor => anchor !== null);
};

export const serializeStructuredCommentState = (anchors: StructuredCommentThreadAnchor[]) =>
  JSON.stringify({
    type: STRUCTURED_COMMENT_STATE_TYPE,
    anchors,
  } satisfies PersistedStructuredCommentState);

export const parseStructuredCommentState = (
  value: string | undefined
): PersistedStructuredCommentState | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as PersistedStructuredCommentState;

    if (
      parsed?.type !== STRUCTURED_COMMENT_STATE_TYPE ||
      !Array.isArray(parsed.anchors) ||
      !parsed.anchors.every(
        (anchor) =>
          typeof anchor?.threadId === 'string' &&
          typeof anchor?.start?.segmentId === 'string' &&
          typeof anchor?.start?.offset === 'number' &&
          typeof anchor?.end?.segmentId === 'string' &&
          typeof anchor?.end?.offset === 'number'
      )
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const structuredCommentSegment = (
  segmentId: string,
  text: string,
  format?: number,
  style?: string
): StructuredCommentSegmentToken => ({
  kind: 'segment',
  segmentId,
  text,
  format,
  style,
});

export const structuredCommentText = (
  text: string,
  format?: number,
  style?: string
): StructuredCommentTextToken => ({
  kind: 'text',
  text,
  format,
  style,
});
